import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import Toast from "../Toast";
import { sweetConfirm } from "../utils/sweetAlerts";

const API = "/backend/public";

const pageStyle = {
  display: "grid",
  gap: "22px",
};

const heroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 50%, #c2410c 100%)",
  color: "#fff",
  borderRadius: "28px",
  padding: "28px",
  boxShadow: "0 28px 60px rgba(78, 27, 36, 0.22)",
};

const cardStyle = {
  background: "#ffffff",
  borderRadius: "22px",
  padding: "22px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
};

const thStyle = {
  textAlign: "left",
  padding: "14px 12px",
  color: "#7c2d3a",
  borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
  fontSize: "13px",
  background: "rgba(255,247,237,0.72)",
};

const tdStyle = {
  padding: "14px 12px",
  borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
  color: "#334155",
  fontSize: "13px",
  lineHeight: 1.6,
  fontWeight: 500,
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: "14px",
  padding: "10px 12px",
  color: "#0f172a",
  background: "#fff",
  height: "44px",
};

const initialForm = {
  percent_from: "",
  percent_to: "",
  grade_name: "",
  grade_point: "",
};

function GradePointSettingPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(timer);
  }, [toast]);

  const showPopupMessage = (type, title, message) => {
    setToast({ type, title, message });
  };

  const loadRows = async () => {
    const res = await axios.get(`${API}/api/grade-points`);
    setRows(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    loadRows().catch(() => {});
  }, []);

  const sortedPreview = useMemo(
    () => [...rows].sort((a, b) => Number(a.percent_from || 0) - Number(b.percent_from || 0)),
    [rows]
  );

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        percent_from: Number(form.percent_from || 0),
        percent_to: Number(form.percent_to || 0),
        grade_name: form.grade_name,
        grade_point: Number(form.grade_point || 0),
      };

      await axios.post(editingId ? `${API}/api/grade-points/${editingId}` : `${API}/api/grade-points`, payload);
      await loadRows();
      resetForm();
      showPopupMessage("success", editingId ? "Grade Updated" : "Grade Saved", editingId ? "Grade point updated successfully." : "Grade point saved successfully.");
    } catch (error) {
      showPopupMessage("error", "Save Failed", error.response?.data?.message || "Failed to save grade point.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      percent_from: item.percent_from ?? "",
      percent_to: item.percent_to ?? "",
      grade_name: item.grade_name || "",
      grade_point: item.grade_point ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!(await sweetConfirm("Are you sure you want to delete this grade point?", { confirmButtonText: "Yes, Delete" }))) return;
    try {
      await axios.delete(`${API}/api/grade-points/${id}`);
      await loadRows();
      if (editingId === id) {
        resetForm();
      }
      showPopupMessage("success", "Grade Deleted", "Grade point deleted successfully.");
    } catch (error) {
      showPopupMessage("error", "Delete Failed", error.response?.data?.message || "Failed to delete grade point.");
    }
  };

  return (
    <DashboardLayout>
      <Toast
        message={toast?.message}
        title={toast?.title}
        type={toast?.type}
        onClose={() => setToast(null)}
      />
      <div style={pageStyle}>
        <section style={heroStyle}>
          <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.78 }}>
            Certification Workspace
          </p>
          <h1 style={{ margin: "10px 0 12px", fontSize: "34px", lineHeight: 1.1 }}>Grade Point Setting</h1>
          <p style={{ margin: 0, color: "rgba(255,255,255,0.84)", lineHeight: 1.75, maxWidth: "920px" }}>
            New grade point entry, percent range, grade name, and grade point এখান থেকে save হবে। এই same grade mapping পরে marksheet এবং certificate output-er moddheo use kora jabe.
          </p>
        </section>

        <section style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "18px" }}>
            <div>
              <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>{editingId ? "Edit Grade Point" : "New Grade Point Entry"}</h3>
              <p style={{ margin: 0, color: "#64748b" }}>Enter percent range and set the matching grade with grade point.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: "12px", alignItems: "end" }}>
              <div>
                <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 700 }}>Percent From</label>
                <input type="number" min="0" max="100" step="0.01" style={inputStyle} value={form.percent_from} onChange={(event) => setForm((prev) => ({ ...prev, percent_from: event.target.value }))} required />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 700 }}>Percent To</label>
                <input type="number" min="0" max="100" step="0.01" style={inputStyle} value={form.percent_to} onChange={(event) => setForm((prev) => ({ ...prev, percent_to: event.target.value }))} required />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 700 }}>Grade</label>
                <input style={inputStyle} value={form.grade_name} onChange={(event) => setForm((prev) => ({ ...prev, grade_name: event.target.value }))} placeholder="A+, A, B..." required />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: 700 }}>Grade Point</label>
                <input type="number" min="0" max="10" step="0.01" style={inputStyle} value={form.grade_point} onChange={(event) => setForm((prev) => ({ ...prev, grade_point: event.target.value }))} placeholder="10.00" required />
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <button
                  type="submit"
                  className="btn"
                  disabled={saving}
                  style={{
                    background: "linear-gradient(135deg, #166534 0%, #16a34a 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "12px",
                    height: "44px",
                    padding: "0 16px",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {saving ? "Saving..." : editingId ? "Update" : "Save"}
                </button>
                {editingId ? (
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </div>
          </form>
        </section>

        <section style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "18px" }}>
            <div>
              <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Saved Grade Point List</h3>
              <p style={{ margin: 0, color: "#64748b" }}>These saved grade points can be used later inside marksheet and certificate rendering.</p>
            </div>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => loadRows()}>
              Refresh
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Sl No</th>
                  <th style={thStyle}>Percent Range</th>
                  <th style={thStyle}>Grade</th>
                  <th style={thStyle}>Grade Point</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedPreview.map((item, index) => (
                  <tr key={item.id}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{Number(item.percent_from || 0).toFixed(2)}% - {Number(item.percent_to || 0).toFixed(2)}%</td>
                    <td style={tdStyle}><strong style={{ color: "#0f172a" }}>{item.grade_name}</strong></td>
                    <td style={tdStyle}><strong style={{ color: "#0f172a" }}>{Number(item.grade_point || 0).toFixed(2)}</strong></td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(item)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(item.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!sortedPreview.length ? (
                  <tr>
                    <td style={tdStyle} colSpan="5">No grade point entry found yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

export default GradePointSettingPage;



